(ns hai.tasks.mini-app
  "The tasks mini-app card shared by the home dashboard and the extension
   sidebar: the mini-app frame with the perforation shifted right and the
   tasks-tinted header bar, and the two views the card swaps its list for —
   Add a task, and a task open in it. Hosts bring the card frame, the sizes,
   and the data wiring; everything a user sees comes from here. Mirrors
   [[hai.notes.mini-app]]."
  (:require
   [hai.entity.draft :as draft]
   [hai.entity.kebab :as kebab]
   [hai.entity.sections :as sections]
   [hai.mini-apps.frame :as frame]
   [hai.sharing.helper :as sharing]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.ui :as ui]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(o/defstyled card frame/card
  [:&::before {:left "auto" :right "40px"}]
  [header/bar
   {:border-top-left-radius "16px"
    :border-top-right-radius "16px"}]
  ;; Collapsed, the bar is the whole card, so it rounds its bottom corners too.
  [:&.collapsed
   [header/bar
    {:border-bottom-left-radius "16px"
     :border-bottom-right-radius "16px"}]]
  ;; Rows float as inset cards rather than a bordered list. The gap separates
  ;; them now that every card is bordered. Same 8px the board's lanes use. The
  ;; inset holds them off the section edges.
  [:.section-tasks
   {:display "flex" :flex-direction "column" :gap "8px"
    :padding "4px 14px 16px"}]
  ;; The shared frame clips the last section to round the card's bottom; with
  ;; inset floating cards there is nothing to clip, and the clip would eat their
  ;; edges. Let it overflow instead.
  [frame/section
   [:&:last-child
    {:overflow "visible"}
    ;; Without that clip a collapsed last section sits on the card's bottom edge
    ;; with nothing rounding it, so its hover fill rounds its own corners. Open,
    ;; the rows are what reach the edge and the toggle stays square.
    [frame/section-toggle
     ["&:not(.open)"
      {:border-bottom-left-radius "16px"
       :border-bottom-right-radius "16px"}]]]])

;; The column the urgency sections stack in: the frame's scrolling list body,
;; flush, since the sections carry their own spacing. It fills the clip around
;; it, which the host sizes.
(o/defstyled sections frame/list-body
  {:padding "0" :gap "0"})

;;
;; Add a task, and a task open in the card
;;

;; What the card holds under the header in place of the list: the same clip
;; the list sits in, so the card keeps its height across the switch, scrolled
;; when the form outgrows it. Standard scrollbar props only — see
;; [[frame/list-body]].
(o/defstyled editor-pane :div
  {:flex "1 1 auto" :min-height "0"
   :overflow-y "auto"
   :overflow-x "hidden"
   :scrollbar-width "thin"
   :scrollbar-color "var(--note-sage-55) var(--card-forest-10)"})

;; The ⋮ in the tinted header, sized and coloured like the bar's round buttons.
(o/defstyled bar-kebab :span
  {:display "inline-flex"}
  [sections/kebab-wrap
   [:button
    {:width "28px" :height "28px" :border-radius "999px"
     :color t/--note-kraft}
    [:&:hover {:background t/--note-kraft-10 :color t/--note-kraft}]]])

(defn- assignee-picker-slot [{:keys [on-pick trigger]}]
  [assignee-picker/component
   {:editable? true
    :on-pick on-pick
    :label "Assign"
    :trigger trigger}])

(defn composer-view
  "Add-a-task mode: the header retitled, with ✕, over the composer on the
   host's `draft-id`. `on-save` gets the staged fields — the host creates the
   task and closes the composer once it lands; `on-close` is ✕, Cancel, and
   Escape in the collapsed title. `saving?` is the host's create in flight;
   `show-hint?` shows the keyboard shortcut on Save (off for touch)."
  [{:keys [draft-id saving? show-hint? on-save on-close]}]
  [:<>
   [header/bar {:title "Add a task"}
    [:button.bar-action-btn
     {:type "button"
      :aria-label "Close"
      :title "Close"
      :on-click on-close}
     [icons/outlined-close {:size 18}]]]
   [frame/list-clip
    [editor-pane
     [form/task-form
      {:draft-id draft-id
       :default-urgency @(<s [::data/default-urgency])
       :saving? saving?
       :show-hint? show-hint?
       :on-save on-save
       :on-cancel on-close
       :on-close on-close
       :render-assignee-picker assignee-picker-slot}]]]])

(defn editing-view
  "A task open over the card: \"← Back to tasks\" in the header, and the ⋮
   holding Delete — disabled, saying why, for anyone but the task's owner —
   over the composer on `task`, or the same layout read-only for a requester
   who may not edit it; either way the stamp sits right of the title, pressable
   by the owner and the assignee. The editor stays up across a save; Back and
   Delete put the list back.

   Surface differences are passed in: `show-hint?` (the Save shortcut, off for
   touch), `spaces?` (the spaces half of the tag row), `on-open-entity` (routes
   a space chip's click), `task-link` (links the \"Task saved\" toast to the
   task) and `change-fx` (rides the save's, the stamp's and the delete's
   success)."
  [{:keys [task show-hint? spaces? on-open-entity task-link change-fx]}]
  (let [task-uuid (:task/uuid task)
        done? (some? (:task/completed-at task))
        current-user @(<s [:auth/identity])
        saving? (= :start @(<s [:fetch/state [::data/save-edit task-uuid]]))
        close! #(>e [::data/close-editor])]
    [:<>
     [header/bar {:on-back close!}
      [bar-kebab
       [kebab/menu
        {:on-delete (fn []
                      (close!)
                      (>e [::data/delete task-uuid change-fx]))
         :delete-label "Delete task"
         :confirm-prompt "Delete this task?"
         :delete-disabled-reason (when-not (sharing/owner? task)
                                   "Only the owner can delete this task")
         :aria-label "Task actions"}]]]
     [frame/list-clip
      [editor-pane
       [form/task-form
        {:editing? (sharing/can-edit? task)
         :draft-id data/editor-draft-id
         :task task
         :start-expanded? true
         :show-hint? show-hint?
         :saving? saving?
         :spaces? spaces?
         :on-open-entity on-open-entity
         :done? done?
         :title-action [ui/stampbox
                        {:done?      done?
                         :task-title (:task/title task)
                         :disabled?  (not (data/can-complete? current-user task))
                         ;; Re-completing re-stamps: a new completed-at remounts
                         ;; the stamp so its jitter is rolled again.
                         :stamp-key  (:task/completed-at task)
                         :on-toggle  #(>e [(if done? ::data/uncomplete ::data/complete) task-uuid
                                           change-fx])}]
         ;; The slice reloads after the save, the way the board re-resolves its
         ;; rows: a tag typed here is patched in without a uuid, and only server
         ;; truth can give the next save something to diff against.
         :on-save (fn [payload]
                    (>e [::data/save-edit task
                         (assoc payload
                                :task-link task-link
                                :success-fx (into [[:dispatch [::data/load-owned-or-assigned-tasks]]]
                                                  change-fx))]))
         :on-cancel #(>e [::draft/seed data/editor-draft-id (form/from-task task)])
         :on-close close!
         :assignable? (data/can-assign? task)
         :render-assignee-picker assignee-picker-slot}]]]]))
